import React, { useEffect, useState } from "react";
import "../styles/MemberDetails.css";
import { Link, useLocation, useNavigate } from "react-router-dom";
import SideBar from "../components/SideBar";
import "../styles/WalletPage.css";
import noRecordsImg from "../assets/images/no-record-found 3.png";
import "../styles/CustomerUnitsTransactionHistory.css";
import { memberService } from "../services/MemberServices";

function MemberUnitsTransactionHistory() {
  const location = useLocation();
  const member = location.state?.member;
  const navigate = useNavigate();

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pagination, setPagination] = useState({});
  const [summary, setSummary] = useState({});

  const [currentPage, setCurrentPage] = useState(1);
  const [transactionType, setTransactionType] = useState("all");

  const rowsPerPage = 50; // Show more records per page

  const goToWallet = () =>
    navigate("/member-wallet-details", { state: { member } });
  const goToRequestHistory = () =>
    navigate("/member-cash-request-history", { state: { member } });
  const goToMemberDetails = () =>
    navigate("/member-details", { state: { member } });

  const fetchTransactionHistory = async (page = 1) => {
    if (!member?.id) {
      console.error("No member ID found:", member);
      return;
    }

    setLoading(true);
    setError("");
    try {
      const params = {
        page,
        per_page: rowsPerPage,
        transaction_type: transactionType,
      };

      console.log(
        "Fetching transactions for member:",
        member.id,
        "with params:",
        params
      );

      const response = await memberService.getMemberTransactionHistory(
        member.id,
        params
      );

      console.log("API Response:", response); // Debug the response

      setTransactions(response.transactions || []);
      setPagination(response.pagination || {});
      setSummary(response.summary || {});
    } catch (err) {
      console.error("API Error:", err);
      setError(err.message || "Failed to fetch transaction history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
    fetchTransactionHistory(1);
  }, [transactionType]);

  useEffect(() => {
    fetchTransactionHistory(currentPage);
  }, [currentPage]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-GB", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  if (!member) return <p>No member selected.</p>;

  return (
    <div className="member-details-layout">
      <SideBar />
      <div className="member-details-main">
        {/* Header */}
        <div className="details-header">
          <h2>Member Details</h2>
          <p>You can edit your details to update your profile</p>
        </div>

        {/* Breadcrumb */}
        <div className="breadcrumb">
          <Link to="/dashboard" className="breadcrumb-link">
            Home
          </Link>
          <span className="breadcrumb-separator">{">"}</span>
          <Link to="/manage-member" className="breadcrumb-link">
            Manage Member
          </Link>
          <span className="breadcrumb-separator">{">"}</span>
          <span className="breadcrumb-current">Member Details</span>
        </div>

        {/* Top buttons */}
        <div className="top-bar">
          <div className="top-buttons">
            <button className="tab-btn" onClick={goToMemberDetails}>
              Personal Details
            </button>
            <button className="tab-btn active" onClick={goToWallet}>
              Wallet Details
            </button>
            <button className="tab-btn" onClick={goToRequestHistory}>
              Cash Request History
            </button>
          </div>
        </div>

        {/* Transaction Section */}
        <div className="transaction-container">
          <div className="transaction-header">
            <h2 className="title">Transaction History</h2>
            <div className="transaction-count">
              {pagination.total || 0}{" "}
              {pagination.total === 1 ? "Transaction" : "Transactions"}
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="transaction-content">
            {loading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Loading transactions...</p>
              </div>
            ) : transactions.length > 0 ? (
              <>
                <div className="transaction-table-container">
                  <div className="table-header">
                    <div className="cell date">Date</div>
                    <div className="cell type">Type</div>
                    <div className="cell description">Description</div>
                    <div className="cell units">Amount</div>
                  </div>
                  <div className="table-body">
                    {transactions.map((txn, idx) => (
                      <div className="table-row" key={txn.id || idx}>
                        <div className="cell date" data-label="Date">
                          {txn.date
                            ? new Date(txn.date).toLocaleDateString("en-GB", {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              })
                            : "N/A"}
                        </div>
                        <div className="cell type" data-label="Type">
                          <span
                            className={`type-badge ${txn.transaction_type.toLowerCase()}`}
                          >
                            {txn.transaction_type}
                          </span>
                        </div>
                        <div
                          className="cell description"
                          data-label="Description"
                        >
                          {txn.description || "No description"}
                        </div>
                        <div
                          className={`cell units ${
                            txn.transaction_type === "debit"
                              ? "negative"
                              : "positive"
                          }`}
                          data-label="Amount"
                        >
                          {txn.transaction_type === "credit" ? "+" : "-"}
                          {Math.abs(txn.amount)} Units
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Pagination - Only show if multiple pages */}
                {pagination.last_page > 3 && (
                  <div className="transaction-pagination">
                    <button
                      className="page-btn"
                      onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                      disabled={currentPage === 1 || loading}
                    >
                      &lt;
                    </button>

                    {/* Show limited page numbers for better UX */}
                    {Array.from(
                      { length: Math.min(5, pagination.last_page) },
                      (_, i) => {
                        let pageNum;
                        if (pagination.last_page <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= pagination.last_page - 2) {
                          pageNum = pagination.last_page - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }

                        return (
                          <button
                            key={pageNum}
                            className={`page-btn ${
                              currentPage === pageNum ? "active" : ""
                            }`}
                            onClick={() => setCurrentPage(pageNum)}
                            disabled={loading}
                          >
                            {pageNum}
                          </button>
                        );
                      }
                    )}

                    <button
                      className="page-btn"
                      onClick={() =>
                        setCurrentPage((p) =>
                          Math.min(p + 1, pagination.last_page)
                        )
                      }
                      disabled={currentPage === pagination.last_page || loading}
                    >
                      &gt;
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="no-records">
                <img src={noRecordsImg} alt="No records" />
                <h3>No transactions found</h3>
                <p>No transaction records found for this member.</p>
              </div>
            )}
          </div>

          <div className="back-link">
            <Link to="/member-wallet-details" state={{ member }}>
              Back to Wallet &lt;&lt;
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MemberUnitsTransactionHistory;
